"use client";

import { getProductListing } from "@/services/api/product/listing";
import ProductCard from "@/features/cake-customization/ProductCard";
import { Product } from "@/types/product";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { useMemo } from "react";

const ExploreMoreFromVendor = ({
  vendorId,
  productSlug,
  vendorName,
  vendorLink,
}: {
  vendorId: number;
  productSlug: string;
  vendorName: string;
  vendorLink: string;
}) => {
  const modifiedSearchParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("vendor_id", vendorId.toString());
    return params;
  }, [vendorId]);

  const { data } = useQuery({
    queryKey: ["explore-more-from-vendor", vendorId],
    queryFn: () =>
      getProductListing({
        searchParams: modifiedSearchParams,
        page: 1,
        hasThemeCake: true,
      }),
    select: (data) =>
      data?.results?.length > 0
        ? data.results
            .filter(
              (product: Product) =>
                product.slug !== productSlug && product.in_stock !== false,
            )
            .slice(0, 6)
        : [],
    enabled: vendorId === 0 ? true : !!vendorId,
  });

  if (!data || data.length === 0) return null;

  return (
    <section className="w-full py-8 lg:py-16 px-4 lg:px-12 bg-white overflow-hidden relative">
      <div className="max-w-[1440px] mx-auto">
        {/* Editorial Header */}
        <div className="flex flex-col md:flex-row justify-between items-center md:items-end border-b border-[#E5E0D8] pb-6 md:pb-8 gap-5 md:gap-8 mb-8 md:mb-10 text-center md:text-left">
          <div className="max-w-xl flex flex-col items-center md:items-start w-full">
            <span className="uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#9A8471] text-[10px] md:text-xs font-semibold mb-3 md:mb-4 flex items-center justify-center md:justify-start gap-3 md:gap-4 w-full">
              <span className="w-8 md:w-12 h-[1px] bg-[#9A8471]"></span>
              The Artisan Collection
              <span className="w-8 h-[1px] bg-[#9A8471] md:hidden"></span>
            </span>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-serif leading-tight text-[#1C1C1C]">
              More by{" "}
              <span className="italic text-[#9A8471]">{vendorName}</span>
            </h2>
          </div>

          <Link
            href={vendorLink}
            className="hidden md:flex group items-center justify-center md:justify-start gap-3 md:gap-4 text-[10px] md:text-xs font-bold uppercase tracking-[0.1em] md:tracking-[0.15em] text-[#1C1C1C] hover:text-[#9A8471] transition-colors shrink-0 mt-2 md:mt-0"
          >
            <span className="transition-colors text-[#735c00]">View</span>
            <ArrowRight className="w-3 h-3 md:w-4 md:h-4 transition-transform group-hover:translate-x-2 shrink-0" />
          </Link>
        </div>

        {/* Product Grid */}
        <div className="hidden lg:grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-1 sm:gap-6 lg:gap-8">
          {data.slice(0, 9).map((product: Product) => {
            if (!product) return null;
            return (
              <div
                key={product.slug}
                className="transition-transform duration-300 hover:-translate-y-1"
              >
                <ProductCard product={product} productPage={true} />
              </div>
            );
          })}
        </div>
        <div className="grid lg:hidden grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-1 sm:gap-6 lg:gap-8">
          {data.slice(0, 9).map((product: Product) => {
            if (!product) return null;
            return (
              <div
                key={product.slug}
                className="transition-transform duration-300 hover:-translate-y-1"
              >
                <ProductCard product={product} />
              </div>
            );
          })}
        </div>

        {/* Mobile CTA (Bottom) */}
        <div className="flex md:hidden justify-center mt-8 md:mt-10 pt-6 border-t border-[#E5E0D8]">
          <Link
            href={vendorLink}
            className="group flex items-center justify-center gap-3 text-xs font-bold uppercase tracking-[0.15em] text-[#1C1C1C] hover:text-[#9A8471] transition-colors"
          >
            <span className="transition-colors text-[#735c00]">
              View All From Vendor
            </span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2 shrink-0" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ExploreMoreFromVendor;
